<template>
  <div v-if="!isEmptyData || pageLoading" class="overall-report">
    <InsightHeader
      class="header"
      :loading="pageLoading"
      :reporting-message="reportingMessage"
      @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
    />
    <div class="overall-report-content">
      <OverallReportApplicationsWidget
        class="overall-applications"
        :data="categoryTotals"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <OverallReportHiresWidget
        class="overall-hires"
        :data="categoryTotals"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <OverallReportTable
        class="table"
        :error="error"
        :loading="pageLoading"
        :empty-data="isEmptyData"
        :data="data"
        :table-rows="tableRows"
        :table-totals="tableTotals"
        :downloading="downloading"
        @download-csv="downloadOverallTable"
      />
    </div>
    <FilterExportPreview class="mt-4" />
  </div>
  <NoDataForWidget v-else />
</template>

<script setup>
import {
  onMounted, onBeforeUnmount, computed, ref,
} from 'vue';
import { useFilters } from '@/stores/filters/filters';
import { useRoute, useRouter } from 'vue-router';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useAppBase } from '@/stores/app/base';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { useOverallReport } from '@/stores/insights/overallReport';
import OverallReportApplicationsWidget from '@/components/insights/SourceInsights/OverallReport/OverallReportApplicationsWidget.vue';
import OverallReportHiresWidget from '@/components/insights/SourceInsights/OverallReport/OverallReportHiresWidget.vue';
import OverallReportTable from '@/components/insights/SourceInsights/OverallReport/OverallReportTable.vue';
import { useInsights } from '@/stores/insights/insights';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { useAppAuthData } from '@/stores/app/authData';
import { useInsightsShare } from '@/stores/insights/share';
import { useTracking } from '@/stores/tracking/tracking';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const emit = defineEmits(['track-page-load']);

const {
  isEmptyData, data, tableRows, error, categoryTotals, tableTotals,
} = storeToRefs(useOverallReport());
const { pageLoading } = storeToRefs(useAppLoading());
const { reportingMessage } = storeToRefs(useInsights());
const { dateRange } = storeToRefs(useDatePicker());
const { authData } = storeToRefs(useAppAuthData());

const { loadFilters, setFilterConfig } = useFilters();
const { setDatePickerVariant, resetDateRange } = useDatePicker();
const { loadOverallReport, downloadOverallCSV } = useOverallReport();
const { showFilterBarSettings } = useAppBase();
const { setReportingMessage } = useInsights();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const route = useRoute();
const router = useRouter();

const downloading = ref(false);

const projectName = computed(() => authData.value.projectName);

const loadPage = async (params) => Promise.all([
  loadFilters(params),
  loadOverallReport(params),
]);

const downloadOverallTable = async () => {
  downloading.value = true;

  await downloadOverallCSV({
    dateRange: dateRange.value,
    projectName: projectName.value,
  });

  useTracking().insightsDownloadReport({
    reportName: route.name,
    downloadType: 'Individual Widgets',
    downloadLocation: 'Individual Widget',
    fileType: 'CSV',
    widgetName: 'Data Table (.csv)',
  });

  downloading.value = false;
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  emit('track-page-load', { page: 'Overall Report' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'source',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');
  showFilterBarSettings({ route: route.name, options: [] });

  setShareConfig({
    shareable: true,
    showReportImageButton: true,
  });

  setExportConfig({
    csvExport: true,
    pngExport: true,
    pngWidgetOptions: {
      'overall-top-sources-by-application': 'Graph: Source Distribution of Apps',
      'overall-top-sources-by-hire': 'Graph: Source Distribution of Hires',
      'overall-report-table': 'Table: Overall Source Data',
    },
    csvOptions: [
      {
        label: 'Data Table (.csv)',
        csvDownloadFunction: () => downloadOverallCSV({
          dateRange: dateRange.value,
          projectName: projectName.value,
        }),
      },
    ],
  });

  await loadPage({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', router });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  resetDateRange();

  resetExportConfig();

  resetShareConfig();
});
</script>

<style lang="less" scoped>
.overall-report {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 16px;
}

.overall-report-content {
  width: 100%;
  display: grid;
  grid-template-areas:
    "overallApplications overallHires"
    "table table";
  grid-template-rows: 1fr auto;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.overall-applications {
  grid-area: overallApplications;
}

.overall-hires {
  grid-area: overallHires;
}

.table {
  grid-area: table;
}
</style>
